import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "@/providers";
import { useDomainServices } from "@/providers/DomainServices";

import { viewQueryKeys } from "./viewQueryKeys";

/** Delete a View. Author-only: the backend enforces `can_delete`, which an editor does not hold. */
export function useDeleteView() {
  const { viewService } = useDomainServices();
  const { currentOrganization } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (viewId: string) => {
      if (currentOrganization === null) {
        return Promise.reject(new Error("CurrentOrganization is not set"));
      }
      return viewService.deleteView(viewId, currentOrganization.org_id);
    },
    // Captured when the mutation starts, not when it settles: `onSuccess`
    // reads whatever org is current at completion, and a request that spans an
    // org switch would otherwise invalidate the org the user moved to.
    onMutate: () => ({ orgId: currentOrganization?.org_id }),
    onSuccess: (_, viewId, { orgId }) => {
      void queryClient.invalidateQueries({
        queryKey: viewQueryKeys.lists(orgId),
      });
      queryClient.removeQueries({ queryKey: viewQueryKeys.id(orgId, viewId) });
    },
  });
}
